import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer min-w-[260px] w-[260px] flex-shrink-0"
      >
        <div className="relative w-full h-40 overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <div className="absolute top-2 right-2">
            {article.isHot && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-accent-500 text-background-50 whitespace-nowrap">
                热门
              </span>
            )}
            {article.isNew && !article.isHot && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-500 text-background-50 whitespace-nowrap">
                最新
              </span>
            )}
          </div>
        </div>
        <div className="p-3">
          <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
            {article.title}
          </h3>
          <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
            <span className="whitespace-nowrap">{article.category}</span>
            <span className="whitespace-nowrap">{article.publishedAt}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
    >
      <div className="relative w-full h-48 overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          title={article.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-3 right-3 flex items-center gap-2">
          {article.isHot && (
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-accent-500 text-background-50 whitespace-nowrap">
              热门
            </span>
          )}
          {article.isNew && !article.isHot && (
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-primary-500 text-background-50 whitespace-nowrap">
              最新
            </span>
          )}
        </div>
      </div>
      <div className="p-4">
        <span className="text-xs text-accent-600 font-medium whitespace-nowrap">{article.category}</span>
        <h3 className="mt-1.5 text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 line-clamp-2 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200/70">
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="whitespace-nowrap">{article.author}</span>
            <span className="whitespace-nowrap">{article.publishedAt}</span>
          </div>
          <span className="flex items-center gap-1 text-xs text-foreground-400 whitespace-nowrap">
            <i className="ri-eye-line text-sm"></i>
            {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
          </span>
        </div>
      </div>
    </Link>
  );
}